import { useCallback, useEffect, useState } from "react";

/**
 * Hash-based tab persistence. Survives browser refresh by storing the active
 * tab in `window.location.hash`.
 *
 * Per-route disambiguation: pages can opt into a path-scoped sessionStorage
 * fallback (off by default — if you only have one tab system on a route, the
 * hash alone is enough). The fallback is useful for nested tab systems where
 * the inner tab should be remembered separately from the outer hash.
 *
 * @param {string[]} validTabs   Allowed tab ids.
 * @param {string}   defaultTab  Fallback when neither hash nor storage match.
 * @param {object}   [opts]
 * @param {string}   [opts.storageKey]  If provided, also persists to
 *                                      sessionStorage under this key (used
 *                                      for nested tabs that don't own the
 *                                      route's hash).
 */
export default function useHashTab(validTabs, defaultTab, opts = {}) {
  const { storageKey } = opts;

  const read = () => {
    // 1) Hash wins (URL-driven, shareable, survives reload).
    const hash = (typeof window !== "undefined" ? window.location.hash : "")
      .replace("#", "");
    if (validTabs.includes(hash)) return hash;
    // 2) sessionStorage fallback (per-tab, scoped, optional).
    if (storageKey && typeof sessionStorage !== "undefined") {
      const stored = sessionStorage.getItem(storageKey);
      if (stored && validTabs.includes(stored)) return stored;
    }
    return defaultTab;
  };

  const [tab, setTabState] = useState(read);

  const setTab = useCallback(
    (next) => {
      if (!validTabs.includes(next)) return;
      setTabState(next);
      // Only own the hash if this hook is the route's primary tab system.
      // When `storageKey` is set, treat it as a nested tab system and avoid
      // clobbering the outer hash.
      if (!storageKey && typeof window !== "undefined") {
        if (window.location.hash.replace("#", "") !== next) {
          window.location.hash = next;
        }
      }
      if (storageKey && typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(storageKey, next);
      }
    },
    // validTabs / storageKey are expected to be stable; if a caller passes a
    // fresh array each render, we accept the cost — they should memoize.
    [validTabs, storageKey],
  );

  // React to hash changes from history navigation (back/forward, link clicks).
  useEffect(() => {
    if (storageKey) return; // nested tab — hash is not ours
    const onHash = () => {
      const next = window.location.hash.replace("#", "");
      if (validTabs.includes(next) && next !== tab) setTabState(next);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [validTabs, tab, storageKey]);

  return [tab, setTab];
}
